import { useEffect, useState, useCallback, useRef } from 'react';
import { runInAction } from 'mobx';
import InspirationSlider from '@components/InspirationSlider/InspirationSlider';
import ProductCard from '@components/ProductCardNew/ProductCard';
import { SectionHeader } from '@components/SectionHeader';
import { useResponsive } from '@features/responsive';
import ProductState from '@features/categories/mobx/state/ProductState';
import NextCategoriesApi from '@features/api/nextApiService/nextCategoriesApi';
import { PRODUCT_OBJECT_SCOPE, DEFAULT_THUMBNAIL_SIZES } from '@features/categories/const';
import styles from './ProductsCarouselBlock.styles';
import SpacingBlock from '@features/cms/components/collections/SpacingBlock/SpacingBlock';
import theme from '@styles/theme';
import { IProductObject } from '@features/categories/typescript';
export interface ProductsCarouselBlockData {
  title?: string | null;
  categoryId?: number;
  limit?: number;
  backgroundColor?: string | null;
  transparentTiles?: boolean;
  products?: IProductObject[];
  marginTop?: number;
  marginBottom?: number;
  background?: string;
  code?: string;
}
export interface ProductsCarouselBlockProps {
  data: ProductsCarouselBlockData;
}
const ProductsCarouselBlock = ({
  data
}: ProductsCarouselBlockProps) => {
  const {
    title,
    categoryId,
    limit = 12,
    backgroundColor,
    transparentTiles = false,
    products: initialProducts,
    marginTop,
    marginBottom,
    background,
    code
  } = data;
  const isEmpty = (v: string | null | undefined) => !v || String(v).trim() === '';
  const sectionBg = isEmpty(backgroundColor) ? theme.rawColors.neutralWhite : backgroundColor!;
  const sectionBackground = isEmpty(background) ? sectionBg : background!;
  const isWhiteSectionBg = (() => {
    const normalized = sectionBackground.trim().toLowerCase();
    return normalized === '#ffffff' || normalized === '#fff' || sectionBackground === theme.rawColors.neutralWhite;
  })();
  const spacingBg = sectionBg;
  const useWhiteProductCard = !isWhiteSectionBg;
  const {
    isWiderThanTablet
  } = useResponsive();
  const [products, setProducts] = useState<ProductState[]>([]);
  const [isMounted, setIsMounted] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const getSpace = () => isWiderThanTablet ? 24 : 10;
  const fetchProducts = useCallback(async () => {
    try {
      const data = await NextCategoriesApi.getMarketingCategoryProducts(categoryId!, {
        scope: PRODUCT_OBJECT_SCOPE.join(','),
        limit,
        offset: 0,
        thumbnails: DEFAULT_THUMBNAIL_SIZES
      });
      if (data?.products && Array.isArray(data.products) && data.products.length > 0) {
        const fetchedProducts: ProductState[] = [];
        runInAction(() => {
          data.products.forEach((product: IProductObject) => {
            fetchedProducts.push(new ProductState(product));
          });
        });
        setProducts(fetchedProducts);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  }, [categoryId, limit]);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  useEffect(() => {
    if (!isMounted) return;
    if (initialProducts && initialProducts.length > 0) {
      const mapped: ProductState[] = [];
      runInAction(() => {
        initialProducts.forEach(product => {
          mapped.push(new ProductState(product));
        });
      });
      setProducts(mapped);
    } else if (categoryId) {
      fetchProducts();
    }
  }, [isMounted, fetchProducts, categoryId, initialProducts]);
  useEffect(() => {
    if (products.length === 0 || !sliderRef.current) return;
    const swiperEl = sliderRef.current.querySelector('.swiper') as HTMLElement;
    if (!swiperEl) return;
    const pinHeight = () => {
      swiperEl.style.height = '';
      requestAnimationFrame(() => {
        swiperEl.style.height = `${swiperEl.offsetHeight}px`;
      });
    };
    requestAnimationFrame(pinHeight);
    window.addEventListener('resize', pinHeight);
    return () => window.removeEventListener('resize', pinHeight);
  }, [products]);
  if (!products || products.length === 0) {
    return null;
  }
  return <>
      {Number(marginTop) > 0 && <SpacingBlock data={{
      type: 'spacingBlock',
      backgroundColor: sectionBackground ?? spacingBg,
      height: String(Number(marginTop)),
      code: 'mt'
    }} />}
      <section sx={styles.section(sectionBackground)} data-testid={code}>
        <div sx={styles.container}>
          {title && <>
              <SectionHeader title={title} sx={styles.header} />
              <SpacingBlock data={{
            type: 'spacingBlock',
            backgroundColor: sectionBackground,
            height: '40',
            code: 'spacing-1'
          }} />
            </>}

          <div ref={sliderRef} sx={styles.sliderWrapper}>
            <InspirationSlider withScrollBar withoutPadding spaceBetween={getSpace()} slidesPerView="auto" fluidSectionOffset>
              {products.map(product => <div key={product.id} sx={styles.productSlide(useWhiteProductCard)} className="carousel-product-slide" data-testid="ProductsCarouselBlock-productSlide">
                  <ProductCard product={product} isSlider transparent={transparentTiles} isWhiteBg={useWhiteProductCard} />
                </div>)}
            </InspirationSlider>
          </div>
        </div>
      </section>
      {Number(marginBottom) > 0 && <SpacingBlock data={{
      type: 'spacingBlock',
      backgroundColor: sectionBackground ?? spacingBg,
      height: String(Number(marginBottom)),
      code: 'mb'
    }} />}
    </>;
};
export default ProductsCarouselBlock;